/*
 * decaffeinate suggestions:
 * DS102: Remove unnecessary code created because of implicit returns
 * DS201: Simplify complex destructure assignments
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
/* eslint eqeqeq:0 */
import { ApiError } from 'app/scripts/network/api-error';
import { ajaxEvents } from './ajaxEvents';
// eslint-disable-next-line no-restricted-imports
import { Batcher } from 'app/scripts/network/rpc/batcher';
// eslint-disable-next-line no-restricted-imports
import { trelloBatchFetch } from './trelloBatchFetch';
import { getPollLength } from 'app/scripts/network/rpc/getPollLength';
import BluebirdPromise from 'bluebird';
import _ from 'underscore';
import { l } from 'app/scripts/lib/localize';
import { syncError } from 'app/scripts/debug/sync-error';
import { Analytics } from '@trello/atlassian-analytics';
import { showFlag } from '@trello/nachos/experimental-flags';
import { featureFlagClient } from '@trello/feature-flag-client';
import type { AjaxBatchResponse } from './rpc.types';
import { ajaxState } from './ajaxState';

import { makeErrorEnum } from 'app/scripts/lib/make-error-enum';

// eslint-disable-next-line @trello/no-module-logic
const RpcError = makeErrorEnum('RPC', ['SyncError', 'BatchError']);

const tryParseJSON = function (str: string) {
  try {
    return JSON.parse(str);
  } catch (error) {
    return null;
  }
};

const LIMIT_FOR_FAILED_POLLING = 120000;

// Here we use polling instead of any socket or Comet-style interaction, so we have to fake out
// anything that relies on server push (e.g. subscription).
class AjaxRPC {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delegate: any;
  idBoardToPoll: string | null;
  modelIxUpdate: object;
  timeOfFirstFailedPoll: null | number;
  ajaxSessionId: string;
  consecutiveFailures: number;
  consecutiveFailuresAllowed: number;
  totalFailures: number;
  totalFailuresAllowed: number;
  _pollingTimeout: null | undefined | number;
  dateLastMessage: number;

  // The `delegate` argument should be changed to type "RPC" once that has been converted to TypeScript.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(delegate?: any) {
    this.delegate = delegate;
    this.idBoardToPoll = null;
    this.modelIxUpdate = {};
    ajaxState.setValue({
      pollRequestFailures: 0,
    });
    this.timeOfFirstFailedPoll = null;
    this.ajaxSessionId = Math.random().toString().substring(2);
    while (this.ajaxSessionId.length < 16) {
      this.ajaxSessionId += '0';
    }

    this.consecutiveFailures = 0;
    this.consecutiveFailuresAllowed = 300;

    this.totalFailures = 0;
    this.totalFailuresAllowed = 1000;
  }

  subscribe(
    modelType: string,
    idModel: string,
    tags: string[],
    next: (...args: unknown[]) => unknown,
  ) {
    return typeof next === 'function' ? next() : undefined;
  }

  unsubscribe(modelType: string, idModel: string, next?: () => void) {
    return typeof next === 'function' ? next() : undefined;
  }

  stopPolling() {
    ajaxState.setValue({
      isPolling: false,
    });
    if (this._pollingTimeout != null) {
      clearTimeout(this._pollingTimeout);
      return delete this._pollingTimeout;
    }
  }

  poll() {
    ajaxState.setValue({
      isPolling: true,
    });
    return (this._pollingTimeout = window.setTimeout(
      () => {
        if (
          this.consecutiveFailures >= this.consecutiveFailuresAllowed ||
          this.totalFailures >= this.totalFailuresAllowed
        ) {
          ajaxEvents.trigger('connect_failed');
          return;
        }

        return this.doPoll((err) => {
          if (err != null) {
            if (err === 'syncError') {
              // Stop polling; we're currently requesting a refresh
              return;
            }
            ajaxEvents.trigger('connecting');
            ++this.consecutiveFailures;
            ++this.totalFailures;
          } else {
            if (this.consecutiveFailures > 0) {
              ajaxEvents.trigger('reconnect');
            }
            this.consecutiveFailures = 0;
          }

          if (this._pollingTimeout != null) {
            return this.poll();
          }
        });
      },

      getPollLength(ajaxState.value.pollRequestFailures),
    ));
  }

  // SocketRPC uses doPoll to sync up after it loses its connection, and thus
  // needs to override the models to poll for and be the Backbone event emitter
  // for the response messages. It would be nice to restructure this.
  doPoll(...args: [(err?: string) => void]) {
    let idModel;
    const adjustedLength = Math.max(args.length, 1);
    const [idModels] = args.slice(0, adjustedLength - 1);
    const next = args[adjustedLength - 1];
    const subscriptions = [];
    if (_.isArray(idModels)) {
      for (idModel of idModels) {
        subscriptions.push(this.delegate.currentSubscriptions[idModel]);
      }
    } else if (typeof idModels === 'undefined' || idModels === null) {
      for (idModel in this.delegate.currentSubscriptions) {
        const subscription = this.delegate.currentSubscriptions[idModel];
        subscriptions.push(subscription);
      }
    } else {
      return;
    }

    if (!_.isEmpty(subscriptions)) {
      return this._doPoll(subscriptions, next);
    } else {
      return typeof next === 'function' ? next() : undefined;
    }
  }

  async _doPoll(
    subscriptions: {
      modelType: string;
      idModel: string;
      ixLastUpdate: string;
      tags: string[];
    }[],
    next: () => void,
  ) {
    if (featureFlagClient.get('fep.batcher-v2', false)) {
      const data = subscriptions.map(
        ({ modelType, idModel, ixLastUpdate, tags }) => ({
          id: idModel,
          url:
            `/${modelType}/${idModel}/deltas` +
            '?ixLastUpdate=' +
            encodeURIComponent(ixLastUpdate.toString()) +
            '&tags=' +
            encodeURIComponent(tags.join(',')),
        }),
      );
      const urls = data.map((item) => item.url);
      const urlToIdHash = new Map<string, string>();
      data.forEach((item) => urlToIdHash.set(item.url, item.id));

      try {
        const response = await trelloBatchFetch<AjaxBatchResponse>(urls, {
          operationName: 'pollingFallback',
          headers: {
            'X-Trello-Polling': true,
          },
        });

        let syncErrorOccurrence = null;
        // Reset the request failures to 0 so that we can return to the default
        // polling interval.
        ajaxState.setValue({
          pollRequestFailures: 0,
        });
        this.timeOfFirstFailedPoll = null;

        for (const [url, error, result] of response) {
          const idModel = urlToIdHash.get(url) as string;
          if (error || !result) {
            this.delegate.handleInvalidSubscription(idModel);
            continue;
          }

          if (result.syncError !== null && result.syncError !== undefined) {
            syncErrorOccurrence = RpcError.SyncError(result.syncError);
            break;
          }

          this.delegate.setIxLastUpdate(idModel, result.modelIxUpdate[idModel]);

          _.chain(result.messages != null ? result.messages : [])
            .map(tryParseJSON)
            .compact()
            .forEach(({ notify, idModelChannel }) => {
              ajaxEvents.trigger(
                notify.event,
                _.extend({ idModelChannel }, notify),
              );
            });

          this.dateLastMessage = Date.now();
        }

        if (syncErrorOccurrence) {
          syncError(syncError, {
            isUsingSocket: false,
            dateLastMessage: this.dateLastMessage,
          });
          return this.stopPolling();
        }
      } catch (err) {
        // Increment the request failures so that we can exponentially back off
        // the polling interval.
        ajaxState.setValue({
          pollRequestFailures: ajaxState.value.pollRequestFailures + 1,
        });

        // Note the time of the first polling failure so that we can show a
        // redbox if polling fails for over 2 minutes.
        if (this.timeOfFirstFailedPoll === null) {
          this.timeOfFirstFailedPoll = Date.now();
        }

        Analytics.sendOperationalEvent({
          action: 'errored',
          actionSubject: 'pollAjaxRpc',
          source: 'network:ajax',
        });
        const msg = l('alerts.could not connect');
        const timeSinceFirstFailedPoll =
          this.timeOfFirstFailedPoll === null
            ? 0
            : Date.now() - this.timeOfFirstFailedPoll;
        if (timeSinceFirstFailedPoll > LIMIT_FOR_FAILED_POLLING) {
          showFlag({
            id: 'connectionDead',
            title: msg,
            appearance: 'error',
            actions: [
              {
                content: l('alerts.reload page'),
                onClick: () => window.location.reload(),
                type: 'button',
              },
            ],
            isUndismissable: true,
          });
          this.stopPolling();
        }
      } finally {
        next();
      }
      return;
    }

    const batcher = new Batcher({
      headers: {
        'X-Trello-Polling': true,
        'X-Trello-Operation-Source': 'rpc',
        'X-Trello-Operation-Name': 'pollingFallback',
      },
    });

    const batchRequests = subscriptions.map(
      ({ modelType, idModel, ixLastUpdate, tags }) => {
        const url =
          `/${modelType}/${idModel}/deltas` +
          '?ixLastUpdate=' +
          encodeURIComponent(ixLastUpdate.toString()) +
          '&tags=' +
          encodeURIComponent(tags.join(','));
        return batcher
          .request(url)
          .tap((res) => {
            if (res.syncError != null) {
              return BluebirdPromise.reject(RpcError.SyncError(res.syncError));
            }

            this.delegate.setIxLastUpdate(idModel, res.modelIxUpdate[idModel]);

            _.chain(res.messages != null ? res.messages : [])
              .map(tryParseJSON)
              .compact()
              .forEach(({ notify, idModelChannel }) => {
                ajaxEvents.trigger(
                  notify.event,
                  _.extend({ idModelChannel }, notify),
                );
              });

            this.dateLastMessage = Date.now();
          })
          .catch(ApiError, () => {
            // Unlike a SyncError, an ApiError here isn't sufficient for us to
            // stop processing other batch results.
            return this.delegate.handleInvalidSubscription(idModel);
          });
      },
    );

    BluebirdPromise.all(batchRequests)
      .catch(RpcError.SyncError, (err) => {
        syncError(err, {
          isUsingSocket: false,
          dateLastMessage: this.dateLastMessage,
        });
        return this.stopPolling();
      })
      .catch(Batcher.BatchFailure, function () {})
      // When the batch call fails, this is going to be emitted
      // for each inner request and we'll be catching the first
      // one. But we don't care about this -- we'll look for and
      // handle the BatchFailure on the send call instead.
      .done();

    return batcher
      .send()
      .then(() => {
        // Reset the request failures to 0 so that we can return to the default
        // polling interval.
        ajaxState.setValue({
          pollRequestFailures: 0,
        });
        this.timeOfFirstFailedPoll = null;
      })
      .catch(
        Batcher.BatchFailure,
        ({ message, error }: { message: string; error: Error }) =>
          BluebirdPromise.reject(RpcError.BatchError(message, { error })),
      )
      .catch(() => {
        // Increment the request failures so that we can exponentially back off
        // the polling interval.
        ajaxState.setValue({
          pollRequestFailures: ajaxState.value.pollRequestFailures + 1,
        });

        // Note the time of the first polling failure so that we can show a
        // redbox if polling fails for over 2 minutes.
        if (this.timeOfFirstFailedPoll === null) {
          this.timeOfFirstFailedPoll = Date.now();
        }

        Analytics.sendOperationalEvent({
          action: 'errored',
          actionSubject: 'pollAjaxRpc',
          source: 'network:ajax',
        });
        const msg = l('alerts.could not connect');
        const timeSinceFirstFailedPoll =
          this.timeOfFirstFailedPoll === null
            ? 0
            : Date.now() - this.timeOfFirstFailedPoll;
        if (timeSinceFirstFailedPoll > LIMIT_FOR_FAILED_POLLING) {
          showFlag({
            id: 'connectionDead',
            title: msg,
            appearance: 'error',
            actions: [
              {
                content: l('alerts.reload page'),
                onClick: () => window.location.reload(),
                type: 'button',
              },
            ],
            isUndismissable: true,
          });
          this.stopPolling();
        }
      })
      .nodeify(next)
      .done();
  }
}

export { AjaxRPC };
